import {
  resolveNpmDependencies as resolveNpmDependenciesRequest,
  getResolveNpmDependenciesResult,
} from '@wix/ambassador-velo-npm-v1-npm-package-info/http';
import pWaitFor from 'p-wait-for';
import type { AuthState } from '@wix/cli-auth';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { isHttpError } from '@wix/cli-http-client';
import { httpRequest } from '../http-request.js';
import { getDependencies } from '../files/package-json.js';
import { saveWixLock } from '../files/wix.lock.js';
import {
  resolveNpmDependenciesSchema,
  npmDependenciesLockFileResponseSchema,
} from '../schemas.js';

interface RequestOptions {
  authState: AuthState;
}

const waitForInstallationCompleted = async (
  jobId: string,
  { authState }: RequestOptions
) => {
  try {
    return await pWaitFor(
      async () => {
        try {
          const { data } = await httpRequest(
            { type: 'editor', authState },
            getResolveNpmDependenciesResult({ jobId })
          );

          const npmDependenciesLockFileResponse =
            npmDependenciesLockFileResponseSchema.parse(data);

          return pWaitFor.resolveWith({
            wixLockFileUrl:
              npmDependenciesLockFileResponse.result.npmDependenciesLockFile,
          });
        } catch (e) {
          // Continue polling
          if (isHttpError(e) && e.response?.status === 428) {
            return false;
          }
          // Unexpected error - stop polling
          throw e;
        }
      },
      {
        // Poll each 3 seconds
        interval: 3 * 1000,
        // Fail if 2 minutes passed
        timeout: 2 * 60 * 1000,
      }
    );
  } catch (e) {
    throw new CliError({
      code: CliErrorCode.FailedToGetResolveNpmDependenciesResult(),
      cause: e,
      info: { jobId },
    });
  }
};

const fetchAndSaveWixLock = async (url: string, root: string) => {
  const wixLockContent = await httpRequest<string>(
    { type: 'standalone' },
    { url }
  );
  return saveWixLock(root, wixLockContent.data);
};

export const resolveNpmDependencies = async (
  root: string,
  { authState }: RequestOptions
) => {
  try {
    const npmPackageInfos = await getDependencies(root);

    const { data: resolveNpmDependenciesResponse } = await httpRequest(
      { type: 'editor', authState },
      resolveNpmDependenciesRequest({ npmPackageInfos })
    );

    const { jobId } = resolveNpmDependenciesSchema.parse(
      resolveNpmDependenciesResponse
    );

    const { wixLockFileUrl } = await waitForInstallationCompleted(jobId, {
      authState,
    });

    await fetchAndSaveWixLock(wixLockFileUrl, root);
  } catch (e) {
    throw new CliError({
      code: CliErrorCode.FailedToResolveNpmDependencies(),
      cause: e,
    });
  }
};
